import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import Swal from "sweetalert2";
import CashVoucherServices from "../../../services/CashVoucherServices";

const LOGO_URL =
  "https://indigoinfra.in/img/product/product-1739471679615.jpeg";

const COMPANY = "INDIGO INFRAPROJECTS PVT. LTD.";

const ADDRESS =
  "3rd Floor, Plot No. - 12/B, Pocket 3, Sector - 17, Dwarka, New Delhi - 110 075";

const n = (value) => {
  const number = Number(value);
  return Number.isFinite(number) ? number : 0;
};

const fmt = (value) =>
  n(value).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });

const clean = (value) =>
  String(value ?? "")
    .replace(/[^\x20-\x7E\n]/g, " ")
    .trim();

const name = (value) =>
  clean(
    typeof value === "object" && value !== null
      ? value.name || value.location || ""
      : value
  ).toUpperCase();

const dateLabel = (value) =>
  /^\d{4}-\d{2}-\d{2}$/.test(value || "")
    ? `${value.slice(8, 10)}/${value.slice(5, 7)}/${value.slice(0, 4)}`
    : clean(value);

const belowThousand = (value) => {
  const ones = [
    "", "One", "Two", "Three", "Four",
    "Five", "Six", "Seven", "Eight", "Nine",
    "Ten", "Eleven", "Twelve", "Thirteen",
    "Fourteen", "Fifteen", "Sixteen",
    "Seventeen", "Eighteen", "Nineteen"
  ];

  const tens = [
    "", "", "Twenty", "Thirty", "Forty",
    "Fifty", "Sixty", "Seventy", "Eighty", "Ninety"
  ];

  const parts = [];
  let remaining = value;

  if (remaining >= 100) {
    parts.push(`${ones[Math.floor(remaining / 100)]} Hundred`);
    remaining %= 100;
  }

  if (remaining >= 20) {
    parts.push(tens[Math.floor(remaining / 10)]);
    remaining %= 10;
  }

  if (remaining > 0) {
    parts.push(ones[remaining]);
  }

  return parts.join(" ");
};

const integerWords = (value) => {
  if (value === 0) return "Zero";

  const groups = [
    [10000000, "Crore"],
    [100000, "Lakh"],
    [1000, "Thousand"],
    [1, ""]
  ];

  const parts = [];
  let remaining = value;

  for (const [size, label] of groups) {
    const count = Math.floor(remaining / size);

    if (count) {
      const words =
        count >= 1000
          ? integerWords(count)
          : belowThousand(count);

      parts.push(`${words} ${label}`.trim());
    }

    remaining %= size;
  }

  return parts.join(" ");
};

const amountWords = (value) => {
  const totalPaise = Math.round(n(value) * 100);
  const rupees = Math.floor(totalPaise / 100);
  const paise = totalPaise % 100;

  return (
    `Rupees ${integerWords(rupees)}` +
    (paise ? ` and ${integerWords(paise)} Paise` : "") +
    " Only"
  );
};

const loadLogo = async () => {
  try {
    const response = await fetch(LOGO_URL, { mode: "cors" });

    if (!response.ok) return null;

    const blob = await response.blob();

    return await new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch {
    return null;
  }
};

const extractVoucher = (response) => {
  const payload = response?.data?.status
    ? response.data
    : response;

  return (
    payload?.data?.voucher ||
    payload?.voucher ||
    payload?.data?.data?.voucher
  );
};

const approvalDate = (value) => {
  if (!value) return "-";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) return "-";

  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: "Asia/Kolkata",
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hourCycle: "h23"
  }).formatToParts(date);

  const getPart = (type) =>
    parts.find((part) => part.type === type)?.value || "";

  return (
    `${getPart("day")}/${getPart("month")}/${getPart("year")} ` +
    `${getPart("hour")}:${getPart("minute")}:${getPart("second")}`
  );
};

const personName = (value) =>
  value && typeof value === "object"
    ? clean(value.name).toUpperCase() || "-"
    : "-";

const uniquePeopleNames = (people) => {
  const unique = new Map();

  people.forEach((person) => {
    if (!person || typeof person !== "object") return;

    const displayName = clean(person.name).toUpperCase();

    if (!displayName) return;

    const key = person._id
      ? String(person._id)
      : displayName;

    if (!unique.has(key)) {
      unique.set(key, displayName);
    }
  });

  return [...unique.values()].sort((a, b) =>
    a.localeCompare(b)
  );
};

// Builds the existing PDF design without downloading it.
const buildCashVoucherPDF = async (voucherId) => {
  if (!voucherId) {
    throw new Error("Voucher ID is required.");
  }

  const response = await CashVoucherServices.getVoucher(voucherId);
  const voucher = extractVoucher(response);

  if (
    !voucher ||
    !Array.isArray(voucher.entries) ||
    voucher.entries.length === 0 ||
    voucher.entries.some((entry) => !entry)
  ) {
    throw new Error("Voucher entries are unavailable.");
  }

  const actualTotal = voucher.entries.reduce(
    (sum, entry) => sum + n(entry.amount),
    0
  );

  if (Math.abs(actualTotal - n(voucher.totalAmount)) > 0.01) {
    throw new Error(
      "Voucher amount differs from its entries. Please contact Accounts before printing."
    );
  }

  const summary = voucher.daySummary;

  if (
    !summary ||
    !Array.isArray(summary.generatedVoucherNumbers) ||
    !Array.isArray(summary.otherStatusBreakdown) ||
    summary.generatedVoucherCount == null
  ) {
    throw new Error(
      "The updated voucher day summary is unavailable. Please refresh and try again."
    );
  }

  const doc = new jsPDF({
    orientation: "landscape",
    unit: "mm",
    format: "a4",
    compress: true
  });

  const W = doc.internal.pageSize.getWidth();
  const H = doc.internal.pageSize.getHeight();

  const left = 10;
  const right = W - 10;
  const splitX = left + 40;

  const mainX = splitX + 3;
  const mainRight = right - 3;
  const mainW = mainRight - mainX;

  const bottomMargin = 17;
  const logo = await loadLogo();

  const number = clean(voucher.voucherNumber) || "-";
  const revision = n(voucher.revision);
  const voucherDate = dateLabel(voucher.voucherDate);

  const summaryDate = dateLabel(
    summary.transactionDate || voucher.voucherDate
  );

  const passbookOwner = personName(voucher.passbook?.employee);

  const creditName =
    name(voucher.passbook?.employee) ||
    name(voucher.passbook) ||
    "CASHBOOK";

  const projectName = name(voucher.store) || "-";
  const passbookName = name(voucher.passbook) || "-";

  const drawVoucherInformation = (
    isSummaryPage,
    targetDoc = doc
  ) => {
    autoTable(targetDoc, {
      theme: "grid",
      startY: 44,
      tableWidth: mainW,
      margin: {
        left: mainX,
        right: W - mainRight,
        top: 44,
        bottom: bottomMargin
      },
      body: isSummaryPage
        ? [
          [
            `Project: ${projectName}`,
            `CashBook: ${passbookName}`,
            `Owner (CashMaker): ${passbookOwner}`
          ]
        ]
        : [
          [
            `Project: ${projectName}`,
            `Voucher No.: ${number}`
          ],
          [
            `CashBook: ${passbookName}`,
            `Date: ${voucherDate}`
          ],
          [
            `Entries in this voucher: ${voucher.entries.length} out of Total Cash Out Entries: ${summary.totalEntries}`,
            `Revision: R${revision}`
          ]
        ],
      styles: {
        font: "helvetica",
        fontSize: isSummaryPage ? 10 : 7.6,
        cellPadding: isSummaryPage ? 3 : 1.5,
        textColor: [0, 0, 0],
        lineColor: [130, 130, 130],
        lineWidth: 0.15,
        overflow: "linebreak",
        valign: "middle",
        halign: "left"
      },
      columnStyles: isSummaryPage
        ? {
          0: { cellWidth: mainW * 0.24 },
          1: { cellWidth: mainW * 0.40 },
          2: { cellWidth: mainW * 0.36 }
        }
        : {
          0: { cellWidth: mainW * 0.59 },
          1: { cellWidth: mainW * 0.41 }
        },
      rowPageBreak: "avoid",
      pageBreak: "avoid"
    });

    return targetDoc.lastAutoTable.finalY;
  };

  const summaryInformationBottom =
    drawVoucherInformation(true);

  const measurementDoc = new jsPDF({
    orientation: "landscape",
    unit: "mm",
    format: "a4"
  });

  const detailInformationBottom =
    drawVoucherInformation(false, measurementDoc);

  const informationBottom = Math.max(
    summaryInformationBottom,
    detailInformationBottom
  );

  const summaryTop = summaryInformationBottom + 8;
  const detailTop = informationBottom + 8;

  const pageEnds = new Map([
    [1, summaryInformationBottom]
  ]);

  const recordPage = (data) => {
    const page = doc.internal.getCurrentPageInfo().pageNumber;

    pageEnds.set(
      page,
      Math.max(pageEnds.get(page) || 0, data.cursor?.y || 0)
    );
  };

  const recordPosition = (position) => {
    const page = doc.internal.getCurrentPageInfo().pageNumber;

    pageEnds.set(
      page,
      Math.max(pageEnds.get(page) || 0, position)
    );
  };

  const commonTable = {
    theme: "grid",
    tableWidth: mainW,
    margin: {
      left: mainX,
      right: W - mainRight,
      top: detailTop,
      bottom: bottomMargin
    },
    styles: {
      font: "helvetica",
      fontSize: 8,
      cellPadding: 1.7,
      textColor: [0, 0, 0],
      lineColor: [130, 130, 130],
      lineWidth: 0.15,
      overflow: "linebreak",
      valign: "middle"
    },
    headStyles: {
      fillColor: [239, 242, 245],
      textColor: [0, 0, 0],
      fontStyle: "bold",
      halign: "center"
    },
    rowPageBreak: "avoid",
    didDrawPage: recordPage
  };

  const ensureSpace = (
    position,
    needed,
    nextPageTop = detailTop
  ) => {
    if (position + needed > H - bottomMargin) {
      doc.addPage();
      return nextPageTop;
    }

    return position;
  };

  const drawSectionTitle = (title, position) => {
    doc.setFont("helvetica", "bold");
    doc.setFontSize(9);
    doc.setTextColor(0, 0, 0);
    doc.text(title, mainX, position);
    recordPosition(position + 2);
  };

  // ==========================================
  // SUMMARY PAGE
  // ==========================================

  let y = summaryInformationBottom + 3;

  const count = (value) =>
    value == null ? "-" : String(value);

  const amount = (value) =>
    value == null ? "-" : fmt(value);

  const voucherNumbers = summary.generatedVoucherNumbers
    .map((value) => clean(value))
    .filter(Boolean);

  const summaryRows = [
    [
      "Total Cash Out Entries",
      count(summary.totalEntries),
      amount(summary.totalAmount)
    ],
    [
      "Entries in This Voucher",
      count(summary.thisVoucherEntries),
      amount(summary.thisVoucherAmount)
    ],
    [
      "Eligible - Remaining for Generation",
      count(summary.remainingEntries),
      amount(summary.remainingAmount)
    ],
    [
      "Generated Vouchers",
      count(summary.generatedVoucherCount),
      voucherNumbers.join(", ") || "-"
    ],
    [
      "Not Currently Eligible",
      count(summary.otherEntries),
      amount(summary.otherAmount)
    ],
    ...summary.otherStatusBreakdown.map((group) => [
      `Status: ${clean(group.label)}`,
      count(group.count),
      amount(group.amount)
    ])
  ];

  autoTable(doc, {
    ...commonTable,
    startY: y + 3,
    margin: {
      ...commonTable.margin,
      top: summaryTop
    },
    head: [
      ["Details", "Count", "Amount Rs. / Voucher Nos."]
    ],
    body: summaryRows,
    showHead: "everyPage",
    styles: {
      ...commonTable.styles,
      font: "helvetica",
      fontSize: 11,
      cellPadding: {
        top: 3.2,
        right: 3,
        bottom: 3.2,
        left: 3
      },
      minCellHeight: 11,
      halign: "left",
      valign: "middle",
      textColor: [35, 48, 65],
      lineColor: [218, 225, 233],
      lineWidth: 0.15,
      overflow: "linebreak"
    },
    headStyles: {
      fillColor: [30, 51, 75],
      textColor: [255, 255, 255],
      font: "helvetica",
      fontStyle: "bold",
      fontSize: 11,
      halign: "left",
      valign: "middle",
      cellPadding: 3.2,
      lineColor: [30, 51, 75],
      lineWidth: 0.15
    },
    alternateRowStyles: {
      fillColor: [247, 249, 252]
    },
    columnStyles: {
      0: {
        cellWidth: mainW * 0.50,
        halign: "left"
      },
      1: {
        cellWidth: mainW * 0.12,
        halign: "left"
      },
      2: {
        cellWidth: mainW * 0.38,
        halign: "left"
      }
    },
    didParseCell: (data) => {
      data.cell.styles.halign = "left";

      if (data.section !== "body") return;

      const rowIndex = data.row.index;
      const columnIndex = data.column.index;

      if (columnIndex === 1 || columnIndex === 2) {
        data.cell.styles.fontStyle = "bold";
      }

      if (rowIndex === 0) {
        data.cell.styles.fillColor = [238, 243, 249];
        data.cell.styles.textColor = [30, 51, 75];
        data.cell.styles.fontStyle = "bold";
      }

      if (rowIndex === 1) {
        data.cell.styles.fillColor = [232, 244, 255];
        data.cell.styles.textColor = [24, 78, 132];
        data.cell.styles.fontStyle = "bold";
      }

      if (rowIndex === 2) {
        data.cell.styles.fillColor = [255, 247, 225];
        data.cell.styles.textColor = [120, 83, 15];
        data.cell.styles.fontStyle = "bold";
      }

      if (rowIndex === 3 && columnIndex === 2) {
        data.cell.styles.textColor = [24, 78, 132];
      }

      if (rowIndex === 4) {
        data.cell.styles.fillColor = [252, 239, 239];
        data.cell.styles.textColor = [139, 46, 46];
        data.cell.styles.fontStyle = "bold";
      }

      if (rowIndex >= 5) {
        data.cell.styles.fontSize = 10;
        data.cell.styles.textColor = [80, 94, 110];

        if (columnIndex === 0) {
          data.cell.styles.cellPadding = {
            top: 3.2,
            right: 3,
            bottom: 3.2,
            left: 5
          };
        }
      }
    }
  });

  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);

  const noteLines = doc.splitTextToSize(
    "Current totals for this CashBook and transaction date. " +
    "Generated Vouchers shows voucher count and voucher numbers.",
    mainW
  );

  const noteLineHeight = 4;

  y = ensureSpace(
    doc.lastAutoTable.finalY + 5,
    noteLines.length * noteLineHeight + 2,
    summaryTop
  );

  doc.text(noteLines, mainX, y, {
    lineHeightFactor: 1.2
  });

  recordPosition(y + noteLines.length * noteLineHeight);

  const summaryPageCount = doc.internal.getNumberOfPages();

  // ==========================================
  // DEBIT ENTRIES
  // ==========================================

  doc.addPage();
  y = detailTop;

  drawSectionTitle("DEBIT", y);

  const debitRows = voucher.entries.map((entry, index) => {
    const checkerDetails = entry.checkerBypassed
      ? [
        "CASHCHECKER: BYPASSED",
        `${personName(entry.checkerBypassedBy)} (${approvalDate(
          entry.checkerBypassedAt
        )})`
      ]
      : [
        "APPROVED BY CASHCHECKER",
        `${personName(entry.adminReviewedBy)} (${approvalDate(
          entry.adminReviewedAt
        )})`
      ];

    const accountsDetails = [
      "APPROVED BY ACCOUNTS",
      `${personName(entry.accountsReviewedBy)} (${approvalDate(
        entry.accountsReviewedAt
      )})`
    ];

    return [
      String(index + 1),

      [
        name(entry.category) || "UNCATEGORIZED",
        clean(entry.remark) || "-"
      ].join("\n"),

      fmt(entry.amount),

      [
        ...checkerDetails,
        "",
        ...accountsDetails
      ].join("\n").toUpperCase()
    ];
  });

  const requestLabelForRow = (rowIndex) =>
    `Request No: ${clean(voucher.entries[rowIndex]?.requestNumber) || "NA"
    }`;

  // Reserve enough space on the right of each narration cell.
  const requestFontSize = 7;

  const requestLabelWidth = (rowIndex) => {
    doc.saveGraphicsState();
    doc.setFont("helvetica", "normal");
    doc.setFontSize(requestFontSize);

    const width = doc.getTextWidth(
      requestLabelForRow(rowIndex)
    );

    doc.restoreGraphicsState();

    return width;
  };

  autoTable(doc, {
    ...commonTable,
    startY: y + 2,

    head: [
      [
        "Sr.",
        "Narration / Category",
        "Amount Rs.",
        "Approver Details"
      ]
    ],

    body: debitRows,

    foot: [
      ["", "Total Debit", fmt(voucher.totalAmount), ""]
    ],

    showHead: "everyPage",
    showFoot: "lastPage",

    styles: {
      ...commonTable.styles,
      fontSize: 7.5,
      cellPadding: 1.6,
      valign: "top"
    },

    footStyles: {
      fillColor: [245, 245, 245],
      textColor: [0, 0, 0],
      fontStyle: "bold"
    },

    columnStyles: {
      0: {
        cellWidth: 10,
        halign: "center"
      },
      1: {
        cellWidth: mainW - 102,
        halign: "left"
      },
      2: {
        cellWidth: 28,
        halign: "left"
      },
      3: {
        cellWidth: 64,
        halign: "left",
        fontSize: 7
      }
    },

    didParseCell: (data) => {
      if (
        data.section === "body" &&
        data.column.index === 1
      ) {
        data.cell.styles.cellPadding = {
          top: 1.6,
          bottom: 1.6,
          left: 1.6,
          right: requestLabelWidth(data.row.index) + 5
        };
      }
    },

    didDrawCell: (data) => {
      if (
        data.section !== "body" ||
        data.column.index !== 1
      ) {
        return;
      }

      doc.saveGraphicsState();

      doc.setFont("helvetica", "bold");
      doc.setFontSize(requestFontSize);
      doc.setTextColor(0, 0, 0);

      doc.text(
        requestLabelForRow(data.row.index),
        data.cell.x + data.cell.width - 1.6,
        data.cell.y + 1.6 + requestFontSize * 0.3528,
        {
          align: "right"
        }
      );

      doc.restoreGraphicsState();
    }
  });
  // ==========================================
  // CREDIT
  // ==========================================

  y = ensureSpace(doc.lastAutoTable.finalY + 5, 35);
  drawSectionTitle("CREDIT", y);

  autoTable(doc, {
    ...commonTable,
    startY: y + 2,
    pageBreak: "avoid",
    head: [
      ["Sr.", "Entry By / Voucher No.", "Amount Rs."]
    ],
    body: [
      [
        "1",
        `${creditName}\nVoucher No.: ${number}`,
        fmt(voucher.totalAmount)
      ]
    ],
    foot: [
      ["", "Total Credit", fmt(voucher.totalAmount)]
    ],
    showHead: "everyPage",
    showFoot: "lastPage",
    footStyles: {
      fillColor: [245, 245, 245],
      textColor: [0, 0, 0],
      fontStyle: "bold"
    },
    columnStyles: {
      0: {
        cellWidth: 10,
        halign: "center"
      },
      1: {
        cellWidth: mainW - 40,
        halign: "left"
      },
      2: {
        cellWidth: 30,
        halign: "left"
      }
    }
  });

  // ==========================================
  // UNIQUE NAMES AND SIGNATURES
  // ==========================================

  const creatorNames = uniquePeopleNames(
    voucher.entries.map((entry) =>
      entry.createdBy?.name
        ? entry.createdBy
        : entry.employee
    )
  );

  const checkerNames = uniquePeopleNames(
    voucher.entries
      .filter((entry) => !entry.checkerBypassed)
      .map((entry) => entry.adminReviewedBy)
  );

  const accountsNames = uniquePeopleNames(
    voucher.entries.map((entry) => entry.accountsReviewedBy)
  );

  const bypassCount = voucher.entries.filter(
    (entry) => entry.checkerBypassed
  ).length;

  const signatureRowCount = Math.max(
    creatorNames.length,
    checkerNames.length,
    accountsNames.length,
    1
  );

  const signatureRows = Array.from(
    { length: signatureRowCount },
    (_, index) => [
      creatorNames[index] || "",
      checkerNames[index] || "",
      accountsNames[index] || ""
    ]
  );

  y = doc.lastAutoTable.finalY + 3;

  const signatureColumnWidth = mainW / 3;

  autoTable(doc, {
    ...commonTable,
    startY: y,
    tableWidth: mainW,
    pageBreak: "avoid",
    rowPageBreak: "avoid",
    head: [
      ["Created By", "CashChecker", "Accounts"]
    ],
    body: signatureRows,
    showHead: "everyPage",
    styles: {
      ...commonTable.styles,
      fontSize: 8,
      cellPadding: 2,
      halign: "center",
      valign: "top",
      lineColor: [160, 160, 160],
      lineWidth: 0.15
    },
    headStyles: {
      ...commonTable.headStyles,
      fontSize: 8,
      cellPadding: 2,
      halign: "center",
      valign: "middle"
    },
    columnStyles: {
      0: {
        cellWidth: signatureColumnWidth,
        halign: "center"
      },
      1: {
        cellWidth: signatureColumnWidth,
        halign: "center"
      },
      2: {
        cellWidth: signatureColumnWidth,
        halign: "center"
      }
    },
    didParseCell: (data) => {
      if (data.section !== "body") return;

      data.cell.styles.minCellHeight = 24;
      data.cell.styles.cellPadding = {
        top: 3,
        right: 3,
        bottom: 15,
        left: 3
      };

      const hasPerson = Boolean(data.cell.raw);

      const signatureRoles = [
        "CASHMAKER",
        "CASHCHECKER",
        "ACCOUNTS"
      ];

      data.cell.text = hasPerson
        ? [
          String(data.cell.raw).toUpperCase(),
          `(${signatureRoles[data.column.index]})`
        ]
        : [""];

      if (!hasPerson && data.row.index === 0) {
        data.cell.text = [
          data.column.index === 1 && bypassCount > 0
            ? "No CashChecker approval"
            : "-"
        ];
      }

      if (
        data.column.index === 1 &&
        data.row.index === 0 &&
        bypassCount > 0
      ) {
        data.cell.text.push(
          `Bypassed for ${bypassCount} ${bypassCount === 1 ? "entry" : "entries"
          }`
        );
      }
    },
    didDrawCell: (data) => {
      if (
        data.section !== "body" ||
        !data.cell.raw
      ) {
        return;
      }

      const centerX = data.cell.x + data.cell.width / 2;
      const signatureY = data.cell.y + data.cell.height - 7;

      const signatureLineWidth = Math.min(
        40,
        data.cell.width - 12
      );

      doc.saveGraphicsState();

      doc.setDrawColor(100, 100, 100);
      doc.setLineWidth(0.15);

      doc.line(
        centerX - signatureLineWidth / 2,
        signatureY,
        centerX + signatureLineWidth / 2,
        signatureY
      );

      doc.setFont("helvetica", "normal");
      doc.setFontSize(6.5);
      doc.setTextColor(90, 90, 90);

      doc.text(
        "Signature",
        centerX,
        signatureY + 3.5,
        { align: "center" }
      );

      doc.restoreGraphicsState();
    }
  });

  // ==========================================
  // REPEATED HEADERS / RECEIPT / BORDERS
  // ==========================================

  const totalPages = doc.internal.getNumberOfPages();

  const receiptX = left + 3;
  const receiptWidth = splitX - left - 6;
  const receiptCenter = left + (splitX - left) / 2;

  doc.setFont("helvetica", "bold");
  doc.setFontSize(7.5);

  const receiptCompany = doc.splitTextToSize(
    COMPANY,
    receiptWidth
  );

  doc.setFont("helvetica", "normal");
  doc.setFontSize(7.3);

  const words = doc.splitTextToSize(
    `The sum of ${amountWords(voucher.totalAmount)}`,
    receiptWidth
  );

  const companyLineHeight = (7.5 * 25.4 / 72) * 1.2;
  const wordsLineHeight = (7.3 * 25.4 / 72) * 1.2;

  const companyOffset = 18;

  const wordsOffset =
    companyOffset +
    (receiptCompany.length - 1) * companyLineHeight +
    8;

  const amountOffset =
    wordsOffset +
    (words.length - 1) * wordsLineHeight +
    6;

  const receiptHeight = amountOffset + 11;

  const maximumContentBottom = Math.max(
    informationBottom,
    ...pageEnds.values()
  );

  const frameBottom = Math.min(
    H - 10,
    Math.max(
      maximumContentBottom + 10,
      10 + receiptHeight + 12
    )
  );

  const receiptTop =
    10 + (frameBottom - 10 - receiptHeight) / 2;

  for (let page = 1; page <= totalPages; page += 1) {
    doc.setPage(page);

    const isSummaryPage = page <= summaryPageCount;

    if (page > 1) {
      drawVoucherInformation(isSummaryPage);
    }

    doc.setTextColor(0, 0, 0);
    doc.setDrawColor(75, 75, 75);
    doc.setLineWidth(0.2);

    // Company header.
    const headerTop = 12;
    const headerHeight = 17;
    const logoBoxWidth = 36;
    const logoGap = 7;

    let logoDrawn = false;

    if (logo) {
      try {
        const properties = doc.getImageProperties(logo);

        const scale = Math.min(
          logoBoxWidth / properties.width,
          headerHeight / properties.height
        );

        const logoWidth = properties.width * scale;
        const logoHeight = properties.height * scale;

        doc.addImage(
          logo,
          properties.fileType || "JPEG",
          mainX + (logoBoxWidth - logoWidth) / 2,
          headerTop + (headerHeight - logoHeight) / 2,
          logoWidth,
          logoHeight
        );

        logoDrawn = true;
      } catch {
        // Continue without the logo.
      }
    }

    const companyX = logoDrawn
      ? mainX + logoBoxWidth + logoGap
      : mainX;

    const companyWidth = mainRight - companyX - 2;

    doc.setFont("helvetica", "bold");

    let companyFontSize = 19;
    doc.setFontSize(companyFontSize);

    while (
      doc.getTextWidth(COMPANY) > companyWidth &&
      companyFontSize > 10
    ) {
      companyFontSize -= 0.5;
      doc.setFontSize(companyFontSize);
    }

    doc.setTextColor(30, 51, 75);

    doc.text(
      COMPANY,
      companyX,
      headerTop + 6.5
    );

    doc.setFont("helvetica", "normal");

    let addressFontSize = 10;
    doc.setFontSize(addressFontSize);

    while (
      doc.getTextWidth(ADDRESS) > companyWidth &&
      addressFontSize > 7
    ) {
      addressFontSize -= 0.25;
      doc.setFontSize(addressFontSize);
    }

    doc.setTextColor(80, 94, 110);

    doc.text(
      ADDRESS,
      companyX,
      headerTop + 12
    );

    doc.setDrawColor(210, 219, 230);
    doc.setLineWidth(0.3);

    doc.line(
      companyX,
      headerTop + 16,
      mainRight,
      headerTop + 16
    );

    // Page title.
    doc.setFillColor(25, 25, 25);
    doc.rect(mainX, 32, mainW, 7, "F");

    doc.setTextColor(255, 255, 255);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(isSummaryPage ? 12 : 10);

    doc.text(
      isSummaryPage
        ? `CASH VOUCHER SUMMARY - ${summaryDate}`
        : "CASH VOUCHER",
      mainX + mainW / 2,
      36.8,
      { align: "center" }
    );

    // Left receipt.
    doc.setTextColor(0, 0, 0);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(9);

    doc.text(
      "RECEIVED",
      receiptCenter,
      receiptTop + 4,
      { align: "center" }
    );

    doc.setFont("helvetica", "normal");
    doc.setFontSize(7.5);

    doc.text(
      "with thanks from",
      receiptCenter,
      receiptTop + 9,
      { align: "center" }
    );

    doc.setFont("helvetica", "bold");
    doc.setFontSize(7.5);

    doc.text(
      receiptCompany,
      receiptCenter,
      receiptTop + companyOffset,
      {
        align: "center",
        lineHeightFactor: 1.2
      }
    );

    doc.setFont("helvetica", "normal");
    doc.setFontSize(7.3);

    doc.text(
      words,
      receiptX,
      receiptTop + wordsOffset,
      { lineHeightFactor: 1.2 }
    );

    const amountY = receiptTop + amountOffset;

    doc.setDrawColor(75, 75, 75);
    doc.setLineWidth(0.2);
    doc.rect(receiptX, amountY, receiptWidth, 11);

    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);

    doc.text(
      `Rs. ${fmt(voucher.totalAmount)}`,
      receiptCenter,
      amountY + 7,
      {
        align: "center",
        maxWidth: receiptWidth - 3
      }
    );

    doc.rect(
      left,
      10,
      right - left,
      frameBottom - 10
    );

    doc.line(splitX, 10, splitX, frameBottom);

    // Footer.
    doc.setFont("helvetica", "normal");
    doc.setFontSize(6);

    doc.text(
      "Generated From Indigo Store Management System",
      mainX,
      frameBottom - 3
    );

    doc.text(
      `Page ${page} of ${totalPages}`,
      mainRight,
      frameBottom - 3,
      { align: "right" }
    );
  }

  const fileName =
    `${number.replace(/[^a-zA-Z0-9_-]/g, "_")}` +
    `_R${revision}.pdf`;

  doc.setProperties({
    title: fileName,
    subject: "Cash Voucher",
    author: COMPANY
  });

  return {
    blob: doc.output("blob"),
    fileName
  };
};

// ==========================================
// PUBLIC FUNCTION: PREVIEW IN A NEW TAB
// ==========================================

export const previewCashVoucherPDFTest = async (voucherId) => {
  if (!voucherId) {
    await Swal.fire({
      icon: "error",
      title: "Voucher unavailable",
      text: "Voucher ID is required."
    });

    return null;
  }

  // Open synchronously from the click before awaiting API calls.
  const previewWindow = window.open("", "_blank");

  if (!previewWindow) {
    await Swal.fire({
      icon: "warning",
      title: "Preview blocked",
      text: "Please allow pop-ups for this website to preview the voucher."
    });

    return null;
  }

  let previewUrl = null;

  try {
    previewWindow.opener = null;
    previewWindow.document.title = "Cash Voucher Preview";

    previewWindow.document.body.style.cssText = `
      margin: 0;
      min-height: 100vh;
      display: flex;
      align-items: center;
      justify-content: center;
      background: #f5f7fb;
      color: #1e334b;
      font-family: Arial, sans-serif;
      font-size: 16px;
    `;

    previewWindow.document.body.textContent =
      "Preparing cash voucher preview...";

    const { blob, fileName } =
      await buildCashVoucherPDF(voucherId);

    if (previewWindow.closed) {
      return null;
    }

    const pdfFile = new File(
      [blob],
      fileName,
      { type: "application/pdf" }
    );

    previewUrl = URL.createObjectURL(pdfFile);

    // Browser PDF viewer supplies Print and Download controls.
    previewWindow.location.replace(previewUrl);

    const cleanupTimer = window.setInterval(() => {
      if (previewWindow.closed) {
        window.clearInterval(cleanupTimer);
        URL.revokeObjectURL(previewUrl);
      }
    }, 2000);

    return fileName;
  } catch (error) {
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }

    if (!previewWindow.closed) {
      previewWindow.close();
    }

    await Swal.fire({
      icon: "error",
      title: "Unable to open preview",
      text:
        error?.response?.data?.message ||
        error?.message ||
        "Please try again."
    });

    return null;
  }
};

export default previewCashVoucherPDFTest;